import React, { memo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PhoneOff, WifiOff, RefreshCw } from "lucide-react";
import Logo from "../assets/images/dealer-logo-dark.png";
import LogoDark from "../assets/images/dealer-logo.png";
import Orb from "../elements/Orb";
import { useTheme } from "../contexts/ThemeContext";
import {
  BASE_AMPS,
  ERROR_MESSAGES,
  stateColor,
  stateConfig,
} from "./voice/voiceConfig";
import { useVoiceSession } from "./voice/useVoiceSession";

interface VoiceModeScreenProps {
  onClose: () => void;
  session?: string;
}

function VoiceModeScreen({ onClose, session }: VoiceModeScreenProps) {
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const orbBg = isDark ? "#0D0F1A" : "transparent";

  const {
    voiceState,
    errorKind,
    visibleCaptions,
    transcriptScrollRef,
    barsRef,
    connect,
    cleanup,
  } = useVoiceSession(session);

  const handleClose = () => {
    cleanup();
    onClose();
  };

  const cfg = stateConfig[voiceState];
  const accent = stateColor[voiceState];
  const isActive = voiceState === "listening" || voiceState === "speaking";

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="fixed inset-0 z-[200] flex flex-col light-bg dark:bg-[#0D0F1A] overflow-hidden"
    >
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-70 dark:opacity-100" />
        <div
          className="dark:hidden absolute top-[-18%] right-[-12%] w-[560px] h-[560px] rounded-full"
          style={{
            background: "radial-gradient(circle, #22d3ee 0%, #3AC1F3 28%, transparent 68%)",
            filter: "blur(80px)",
            opacity: 0.65,
            animation: "orb-float-1 13s ease-in-out infinite",
          }}
        />
        <div
          className="dark:hidden absolute bottom-[-18%] left-[-12%] w-[500px] h-[500px] rounded-full"
          style={{
            background: "radial-gradient(circle, #a78bfa 0%, #818CF8 30%, transparent 68%)",
            filter: "blur(80px)",
            opacity: 0.55,
            animation: "orb-float-2 17s ease-in-out infinite",
          }}
        />
        <div
          className="hidden dark:block absolute top-[-18%] right-[-12%] w-[560px] h-[560px] rounded-full"
          style={{
            background: "radial-gradient(circle, #3AC1F3 0%, transparent 65%)",
            filter: "blur(90px)",
            opacity: 0.5,
            animation: "orb-float-1 13s ease-in-out infinite",
          }}
        />
        <div
          className="hidden dark:block absolute bottom-[-18%] left-[-12%] w-[520px] h-[520px] rounded-full"
          style={{
            background: "radial-gradient(circle, #818CF8 0%, transparent 65%)",
            filter: "blur(90px)",
            opacity: 0.3,
            animation: "orb-float-2 17s ease-in-out infinite",
          }}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: -14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15, duration: 0.4 }}
        className="relative z-10 flex items-center justify-between px-5 pt-5 pb-3 flex-shrink-0"
      >
        <img src={Logo} alt="logo" className="h-8 hidden dark:block" />
        <img src={LogoDark} alt="logo" className="h-8 dark:hidden" />

        <AnimatePresence mode="wait">
          <motion.div
            key={voiceState}
            initial={{ opacity: 0, scale: 0.88, y: -4 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: 4 }}
            transition={{ duration: 0.25 }}
            className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-wide border"
            style={{ color: accent, borderColor: `${accent}40`, background: `${accent}12` }}
          >
            {voiceState === "error" ? (
              <WifiOff size={11} />
            ) : (
              <motion.span
                className="w-1.5 h-1.5 rounded-full"
                style={{ background: accent }}
                animate={voiceState !== "connecting" ? { opacity: [1, 0.3, 1] } : { opacity: 1 }}
                transition={{ duration: 1, repeat: Infinity }}
              />
            )}
            {voiceState === "connecting"
              ? "Connecting"
              : voiceState === "listening"
                ? "Voice Mode"
                : voiceState === "speaking"
                  ? "Speaking"
                  : "Error"}
          </motion.div>
        </AnimatePresence>
      </motion.div>

      <div className="relative flex-1 flex flex-col items-center justify-center min-h-0 z-10">
        <AnimatePresence>
          {voiceState === "connecting" && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute pointer-events-none"
              style={{ width: "min(400px, 82vw)", height: "min(400px, 82vw)" }}
            >
              <motion.div
                className="absolute inset-0 rounded-full"
                style={{
                  border: "2px solid transparent",
                  borderTopColor: "#3AC1F3",
                  borderRightColor: "#3AC1F3",
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
              />
            </motion.div>
          )}
        </AnimatePresence>

        {voiceState !== "connecting" && (
          <motion.div
            className="cursor-default select-none relative z-10"
            style={{ width: "min(380px, 78vw)", height: "min(380px, 78vw)" }}
            animate={{
              scale: voiceState === "speaking" ? 1.06 : voiceState === "listening" ? 1.02 : 1,
              opacity: voiceState === "error" ? 0.4 : 1,
            }}
            transition={{ type: "spring", stiffness: 200, damping: 22 }}
          >
            <Orb
              hue={cfg.hue}
              hoverIntensity={isActive ? 0.42 : 0.1}
              rotateOnHover={true}
              forceHoverState={cfg.forceHover}
              backgroundColor={orbBg}
            />
          </motion.div>
        )}

        <div className="flex flex-col items-center gap-1.5 mt-4 px-6 text-center">
          <AnimatePresence mode="wait">
            <motion.p
              key={voiceState === "error" && errorKind ? errorKind : cfg.label}
              initial={{ opacity: 0, y: 10, filter: "blur(4px)" }}
              animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
              exit={{ opacity: 0, y: -10, filter: "blur(4px)" }}
              transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
              className="text-xl md:text-2xl font-bold tracking-tight"
              style={{ color: voiceState === "error" ? "#ef4444" : isDark ? "#fff" : "#1e293b" }}
            >
              {voiceState === "error" && errorKind ? ERROR_MESSAGES[errorKind].title : cfg.label}
            </motion.p>
          </AnimatePresence>

          <AnimatePresence mode="wait">
            <motion.p
              key={voiceState === "error" && errorKind ? `detail-${errorKind}` : cfg.sublabel}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.28 }}
              className="text-sm text-gray-500 dark:text-gray-400 max-w-[280px] text-center"
            >
              {voiceState === "error" && errorKind ? ERROR_MESSAGES[errorKind].detail : cfg.sublabel}
            </motion.p>
          </AnimatePresence>

          <div className="mt-2 h-7 flex items-center justify-center">
            <AnimatePresence mode="wait">
              {isActive ? (
                <motion.div
                  key="wave"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.85 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-[3px]"
                >
                  {BASE_AMPS.map((_, i) => (
                    <span
                      key={i}
                      ref={(el) => { barsRef.current[i] = el; }}
                      className="w-[3px] rounded-full"
                      style={{ height: "3px", background: accent, transition: "height 0.05s ease, opacity 0.1s" }}
                    />
                  ))}
                </motion.div>
              ) : voiceState === "connecting" ? (
                <motion.div
                  key="dots"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex items-center gap-1.5"
                >
                  {[0, 0.2, 0.4].map((d, i) => (
                    <motion.span
                      key={i}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: "#3AC1F3" }}
                      animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.1, 0.8] }}
                      transition={{ duration: 1, repeat: Infinity, delay: d }}
                    />
                  ))}
                </motion.div>
              ) : voiceState === "error" && errorKind && ERROR_MESSAGES[errorKind].canRetry ? (
                <motion.button
                  key="retry"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={connect}
                  className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold border border-[#ef4444]/40 text-[#ef4444] bg-[#ef4444]/10 hover:bg-[#ef4444]/20 transition-colors"
                >
                  <RefreshCw size={12} />
                  Retry
                </motion.button>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.3 }}
            className="relative z-10 flex-shrink-0 mx-4 mb-3 rounded-2xl overflow-hidden"
            style={{
              background: isDark ? "rgba(13,15,26,0.70)" : "rgba(255,255,255,0.72)",
              backdropFilter: "blur(20px) saturate(180%)",
              WebkitBackdropFilter: "blur(20px) saturate(180%)",
              border: isDark ? "1px solid rgba(255,255,255,0.07)" : "1px solid rgba(186,230,253,0.60)",
              boxShadow: isDark ? "0 4px 24px rgba(0,0,0,0.25)" : "0 4px 20px rgba(14,165,233,0.08)",
            }}
          >
            <div
              className="px-3 pt-2.5 pb-1 border-b"
              style={{ borderColor: isDark ? "rgba(255,255,255,0.06)" : "rgba(186,230,253,0.35)" }}
            >
              <span className="text-[10px] font-bold uppercase tracking-widest text-gray-400 dark:text-gray-500">
                Live Captions
              </span>
            </div>
            <div
              ref={transcriptScrollRef}
              className="flex flex-col gap-2 px-3 py-3 overflow-y-auto no-scrollbar"
              style={{ maxHeight: "160px", scrollbarWidth: "none", msOverflowStyle: "none" } as React.CSSProperties}
            >
              {visibleCaptions.length === 0 ? (
                <p className="text-xs text-center text-gray-400 dark:text-gray-500 py-2 italic">
                  Captions will appear here as you speak…
                </p>
              ) : (
                visibleCaptions.map((entry) => (
                  <motion.div
                    key={entry.id}
                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ type: "spring", stiffness: 400, damping: 28 }}
                    className={`flex flex-col max-w-[80%] ${entry.role === "user" ? "self-end items-end" : "self-start items-start"}`}
                  >
                    <span
                      className="text-[10px] font-semibold uppercase tracking-wider mb-0.5 opacity-50"
                      style={{ color: entry.role === "user" ? "#3AC1F3" : "#818CF8" }}
                    >
                      {entry.role === "user" ? "You" : "AI"}
                    </span>
                    <div
                      className="px-3 py-1.5 rounded-2xl text-xs leading-relaxed"
                      style={entry.role === "user" ? {
                        background: isDark ? "rgba(58,193,243,0.18)" : "rgba(58,193,243,0.12)",
                        border: `1px solid ${entry.interim ? "rgba(58,193,243,0.15)" : "rgba(58,193,243,0.28)"}`,
                        color: isDark ? (entry.interim ? "#7dd3fc" : "#bae6fd") : (entry.interim ? "#38bdf8" : "#0369a1"),
                        borderBottomRightRadius: "4px",
                        opacity: entry.interim ? 0.7 : 1,
                      } : {
                        background: isDark ? "rgba(129,140,248,0.18)" : "rgba(129,140,248,0.12)",
                        border: "1px solid rgba(129,140,248,0.28)",
                        color: isDark ? "#ddd6fe" : "#4338ca",
                        borderBottomLeftRadius: "4px",
                      }}
                    >
                      {entry.role === "assistant" && entry.speaking && !entry.text ? (
                        <span className="flex items-center gap-1 py-0.5">
                          {[0, 0.2, 0.4].map((d, i) => (
                            <motion.span
                              key={i}
                              className="w-1.5 h-1.5 rounded-full"
                              style={{ background: isDark ? "#818CF8" : "#6366f1" }}
                              animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8] }}
                              transition={{ duration: 0.9, repeat: Infinity, delay: d }}
                            />
                          ))}
                        </span>
                      ) : (
                        <>
                          {entry.text}
                          {entry.role === "assistant" && entry.text && (entry.speaking || voiceState === "speaking") && (
                            <motion.span
                              className="inline-block w-0.5 h-3 ml-0.5 align-middle rounded-full"
                              style={{ background: isDark ? "#818CF8" : "#6366f1" }}
                              animate={{ opacity: [1, 0.2, 1] }}
                              transition={{ duration: 0.8, repeat: Infinity }}
                            />
                          )}
                        </>
                      )}
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.4 }}
        className="relative z-10 flex-shrink-0 pb-8 md:pb-12 pt-2 md:pt-4 px-4 md:px-6"
      >
        <motion.button
          whileHover={{ scale: 1.07 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleClose}
          title="End voice session"
          className="rounded-2xl flex items-center justify-center"
          style={{
            width: 64,
            height: 64,
            background: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
            boxShadow: "0 8px 24px rgba(239,68,68,0.40)",
          }}
        >
          <PhoneOff size={22} color="white" />
        </motion.button>

        <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-4">
          {voiceState === "connecting"
            ? "Establishing secure connection..."
            : voiceState === "listening"
              ? "Speak naturally — iVin is listening"
              : voiceState === "speaking"
                ? "iVin is generating a response..."
                : errorKind && !ERROR_MESSAGES[errorKind].canRetry
                  ? "Close this panel and try again later."
                  : "Check your connection and try again."}
        </p>
      </motion.div>
    </motion.div>
  );
}

export default memo(VoiceModeScreen);
